import { Application, ProctoringOption } from "@verity/proctoring-option/types";
import styles from "./index.module.css";
import { CategoryOptionsListProps, OptionProps } from "../proctoringOptionsGrid";
import { shouldShowProctoringOption } from "../configurationSummaryList";
import OptionBlock, { OptionBlockContainer, OptionBlockInfo } from "../optionBlock";
import { getUnsupportedToggleText, PROCTORING_OPTION_TEXT } from "../option-text";
import i18nPackage from "../../../i18n/i18n";
import { useId, useState } from "react";
import { useMobileLayout } from "../../../hooks";
import { CoreButton, CoreRangedRadio, RadioOption } from "../../../core";
import { ConditionalWrapper } from "../../../wrapper";
import { CheckBoxMenuSubItem } from "../option-menus/checkBoxMenuSubItem";
import ApplicationListModal from "../../edit-quiz-modals/applicationListModal";

const BLOCK_APPLICATION_MODES = ["off", "block-list", "allow-list", "all"] as const;
type BlockApplicationsMode = (typeof BLOCK_APPLICATION_MODES)[number];

function BlockApplicationsOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	applications,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T> & {
	applications: Application[];
}) {
	const {
		applicationBlockMode,
		blockWindowedApplications,
		hideOtherWindows,
		applicationBlockList,
		allowedApplicationsList,
		oneScreenEnforcementMode,
	} = option;
	const [blockListModalShowing, setBlockListModalShowing] = useState(false);
	const [allowListModalShowing, setAllowListModalShowing] = useState(false);
	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(applicationBlockMode !== "strict");
	const isMobileLayout = useMobileLayout();

	const notAllowed = !isOptionAllowed("applicationBlockMode");
	const disableBlockWindowed = notAllowed || !isOptionAllowed("blockWindowedApplications");
	const disableBlockAll = disableBlockWindowed || !isOptionAllowed("hideOtherWindows");

	let currMode: BlockApplicationsMode;
	if (applicationBlockMode === "off") {
		currMode = "off";
	} else if (hideOtherWindows) {
		currMode = "all";
	} else if (blockWindowedApplications) {
		currMode = "allow-list";
	} else {
		currMode = "block-list";
	}

	const getHideOtherWindowsOptionUpdates = (hide: boolean) => ({
		hideOtherWindows: hide,
		oneScreenEnforcementMode: hide && oneScreenEnforcementMode === "off" ? "normal" : oneScreenEnforcementMode,
	});

	function getRadioIsDisabled(mode: BlockApplicationsMode) {
		let disabled = notAllowed;
		if (!disabled) {
			if (mode === "allow-list") {
				disabled = disableBlockWindowed;
			} else if (mode === "all") {
				disabled = disableBlockAll;
			}
		}
		return disabled;
	}

	function getRadioLabel(mode: BlockApplicationsMode) {
		return (
			<div className={styles.blockApplicationModeLabelContent}>
				{i18nPackage.t(`editQuizPage.configurationStep.restrictApplicationsTSX.blockApplications.modes.${mode}`)}
				{currMode === mode && mode === "block-list" && (
					<CoreButton onClick={() => setBlockListModalShowing(true)}>
						{i18nPackage.t("editQuizPage.configurationStep.restrictApplicationsTSX.blockApplications.viewList")}
					</CoreButton>
				)}
				{currMode === mode && mode === "allow-list" && (
					<CoreButton onClick={() => setAllowListModalShowing(true)}>
						{i18nPackage.t("editQuizPage.configurationStep.restrictApplicationsTSX.blockApplications.viewList")}
					</CoreButton>
				)}
			</div>
		);
	}

	const headerId = useId();

	const radioOptions: RadioOption[] = BLOCK_APPLICATION_MODES.map((mode) => ({
		value: mode,
		label: getRadioLabel(mode),
		disabled: getRadioIsDisabled(mode),
	}));

	function handleBlockAppOnChange(_: React.ChangeEvent<HTMLInputElement>, value: string) {
		const defaultBlockMode = bypassWasChecked ? "normal" : "strict";

		switch (value) {
			case "off":
				updateOption({
					...option,
					applicationBlockMode: "off",
					blockWindowedApplications: false,
					...getHideOtherWindowsOptionUpdates(false),
				});
				break;

			case "block-list":
				updateOption({
					...option,
					applicationBlockMode: applicationBlockMode === "off" ? defaultBlockMode : applicationBlockMode,
					blockWindowedApplications: false,
					...getHideOtherWindowsOptionUpdates(false),
				});
				if (currMode !== "block-list") {
					setBlockListModalShowing(true);
				}
				break;

			case "allow-list":
				updateOption({
					...option,
					applicationBlockMode: applicationBlockMode === "off" ? defaultBlockMode : applicationBlockMode,
					blockWindowedApplications: true,
					...getHideOtherWindowsOptionUpdates(false),
				});
				if (currMode !== "allow-list") {
					setAllowListModalShowing(true);
				}
				break;

			case "all":
				updateOption({
					...option,
					applicationBlockMode: applicationBlockMode === "off" ? defaultBlockMode : applicationBlockMode,
					blockWindowedApplications: true,
					...getHideOtherWindowsOptionUpdates(true),
				});
				break;
		}
	}

	return (
		<>
			<OptionBlockContainer
				disabled={notAllowed}
				disabledMessage={
					notAllowed
						? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
						: getUnsupportedToggleText(
								option,
								"applicationBlockMode",
								applicationBlockMode === "off" ? "normal" : "off",
								applicationBlockMode !== "off",
							)
				}
				horizontalOrientation={horizontalOrientation}
			>
				<OptionBlockInfo
					id={headerId}
					disabled={notAllowed}
					fullDisabled={disabled || notAllowed}
					description={PROCTORING_OPTION_TEXT.applicationBlockMode.description}
					Icon={PROCTORING_OPTION_TEXT.applicationBlockMode.icon}
					contentHeader={PROCTORING_OPTION_TEXT.applicationBlockMode.header}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("applicationBlockMode")}
				/>
				<ConditionalWrapper
					condition={isMobileLayout}
					whenTrue={(children) => <div className={styles.blockApplicationsModesContainer}>{children}</div>}
				>
					<div className={styles.blockApplicationsModes} style={{ width: isMobileLayout ? "fit-content" : undefined }}>
						<CoreRangedRadio
							curValue={currMode}
							onChange={handleBlockAppOnChange}
							radioGroupAriaLabelledby={headerId}
							radioOptions={radioOptions}
							startLabel={i18nPackage.t(
								"editQuizPage.configurationStep.restrictApplicationsTSX.blockApplications.leastRestrictive",
							)}
							endLabel={i18nPackage.t(
								"editQuizPage.configurationStep.restrictApplicationsTSX.blockApplications.mostRestrictive",
							)}
						/>
					</div>
				</ConditionalWrapper>
				{applicationBlockMode !== "off" && (
					<div
						style={{
							marginLeft: isMobileLayout ? "15px" : "45px",
							width: "max-content",
							maxWidth: isMobileLayout ? "100%" : undefined,
							position: "relative",
							zIndex: 1,
						}}
					>
						{isOptionAllowed("applicationBlockMode", "normal") && isOptionAllowed("applicationBlockMode", "strict") && (
							<CheckBoxMenuSubItem
								label={i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.bypassModeText")}
								checked={bypassWasChecked}
								onChange={(checked) => {
									updateOption({
										...option,
										applicationBlockMode: checked ? "normal" : "strict",
									});
									setBypassWasChecked(checked);
								}}
							/>
						)}
					</div>
				)}
			</OptionBlockContainer>
			{blockListModalShowing && (
				<ApplicationListModal
					action="block"
					onClose={() => setBlockListModalShowing(false)}
					allApplications={applications}
					initialSelectedProcessNames={applicationBlockList ?? []}
					onSave={(processNames) =>
						updateOption({
							...option,
							applicationBlockList: processNames,
							applicationBlockMode: processNames.length === 0 ? "off" : applicationBlockMode,
						})
					}
				/>
			)}
			{allowListModalShowing && (
				<ApplicationListModal
					action="allow"
					onClose={() => setAllowListModalShowing(false)}
					allApplications={applications}
					initialSelectedProcessNames={allowedApplicationsList ?? []}
					onSave={(processNames) =>
						updateOption({
							...option,
							allowedApplicationsList: processNames,
							...getHideOtherWindowsOptionUpdates(processNames.length === 0),
						})
					}
				/>
			)}
		</>
	);
}

export default function BlockItemsOptions<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	applications,
	horizontalOrientation = false,
	getPartiallySupportedPlatforms,
}: CategoryOptionsListProps<T> & {
	applications: Application[];
}) {
	const { blockVirtualMachines } = option;
	const { recordApplicationsActivity } = option;

	const blockVirtualMachinesAllowed = isOptionAllowed("blockVirtualMachines");
	const recordApplicationsActivityAllowed = isOptionAllowed("recordApplicationsActivity");
	return (
		<div className={styles.proctorSettingsGrid}>
			{shouldShowProctoringOption("blockVirtualMachines") && (
				<OptionBlock
					name="blockVirtualMachines"
					checked={Boolean(blockVirtualMachines)}
					updateState={(checked) =>
						updateOption({
							...option,
							blockVirtualMachines: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.blockVirtualMachines.description}
					Icon={PROCTORING_OPTION_TEXT.blockVirtualMachines.icon}
					contentHeader={PROCTORING_OPTION_TEXT.blockVirtualMachines.header}
					disabled={!blockVirtualMachinesAllowed}
					fullDisabled={disabled || !blockVirtualMachinesAllowed}
					disabledMessage={
						!blockVirtualMachinesAllowed
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(
									option,
									"blockVirtualMachines",
									!blockVirtualMachines,
									Boolean(blockVirtualMachines),
								)
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("blockVirtualMachines")}
				/>
			)}

			{shouldShowProctoringOption("applicationBlockMode") && (
				<BlockApplicationsOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					applications={applications}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}
			{shouldShowProctoringOption("recordApplicationsActivity") && (
				<OptionBlock
					name="recordApplicationsActivity"
					checked={Boolean(recordApplicationsActivity)}
					updateState={(checked) =>
						updateOption({
							...option,
							recordApplicationsActivity: checked,
						})
					}
					description={PROCTORING_OPTION_TEXT.recordApplicationsActivity.description}
					Icon={PROCTORING_OPTION_TEXT.recordApplicationsActivity.icon}
					contentHeader={PROCTORING_OPTION_TEXT.recordApplicationsActivity.header}
					disabled={!recordApplicationsActivityAllowed}
					fullDisabled={disabled || !recordApplicationsActivityAllowed}
					disabledMessage={
						!recordApplicationsActivityAllowed
							? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
							: getUnsupportedToggleText(
									option,
									"recordApplicationsActivity",
									!recordApplicationsActivity,
									Boolean(recordApplicationsActivity),
								)
					}
					horizontalOrientation={horizontalOrientation}
					partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("recordApplicationsActivity")}
				/>
			)}
		</div>
	);
}
